// IMPORT A PRODUCT CSV INTO THE MASTER (slice import-b).
//
// The second write path from this app into the product master, and the first
// bulk one. It reuses NewProductModal's chrome and NewProductModal's rules --
// the same taxonomy load, the same intersect, the same fail-loud-never-
// optimistic discipline -- because it writes the same records; only the
// quantity differs.
//
// WHY A PREVIEW EXISTS AT ALL, rather than a straight upload. The endpoint is
// ALL OR NOTHING inside one transaction: row 199 being wrong throws away the
// 198 good rows and answers `products[198]: ...`. Uploading a file with a
// knowably-bad row is therefore a guaranteed round trip that achieves nothing.
// Every judgement shown here is made in services/csvImport.ts, so it is the
// same judgement in one place rather than two.
//
// TWO SENDS, AND THE FIRST ONE WRITES NOTHING. validate_only runs the real
// import server-side and rolls it back, which catches what this client cannot
// possibly know -- an archived brand, a column constraint, anything the ORM
// refuses. Only when that comes back clean does the real send go. A dry run
// that merely re-checked what the preview already checked would be reassurance
// nobody should be given.
//
// ⚠️ WHAT THIS SCREEN MUST NEVER DO: dedupe. The endpoint is create-only and
// part_number is deliberately not unique -- two manufacturers really do ship
// different goods under one string -- so re-importing a file DOUBLES the
// catalog. That is said loudly, in the preview, and the choice stays with the
// person. Silently dropping a row would refuse a catalog that is simply true.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Upload, X } from "lucide-react";
import { getTaxonomy, importProducts, searchProducts } from "@/services/api";
import { intersectTaxonomy, emptyIntersectMessage } from "@/services/productTaxonomy";
import {
  parseProductCsv,
  hasBlockingProblem,
  importSpecs,
  parseImportError,
  catalogDuplicateRowNos,
  templateCsv,
  IMPORT_COLUMNS,
  MAX_IMPORT_ROWS,
  type CsvImportResult,
} from "@/services/csvImport";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, overlayMaxWidth } from "@/styles/tokens";

/** What the file input will offer. A .csv the browser has typed as something
 *  else still gets through -- the extension is checked separately, because
 *  Windows reports text/csv, application/vnd.ms-excel and "" for the same
 *  file depending on what is installed. */
const ACCEPTED_CSV_TYPES = ["text/csv", "application/vnd.ms-excel", "text/plain"];

/** Caught here so an oversized file never leaves the browser, exactly as
 *  Settings does for the company logo. 200 rows of 11 short columns is a few
 *  tens of KB; a megabyte is not a product file, it is a mistake, and reading
 *  it into a string would hang the tab before the row cap could say so. */
const MAX_CSV_BYTES = 1024 * 1024;

/** How much of the catalog the duplicate check reads. The endpoint caps at 500
 *  (_MAX_LIST_LIMIT); see the truncation guard where this is used. */
const DUP_SCAN_LIMIT = 500;

/** Preview column widths. Plain numbers, not tokens: they are this table's
 *  schema, the same class as a COLS array's `width` field. */
const COL_ROW_NO = 48;
const COL_PART_NO = 150;
const COL_BRAND = 110;
const COL_CATEGORY = 96;

type DupScan =
  | { status: "idle" }
  | { status: "loading" }
  /** `truncated` means the catalog page came back AT the limit, so parts
   *  beyond it were never compared. The count is then not zero, it is
   *  UNKNOWN -- see where this is rendered. */
  | { status: "ok"; rowNos: number[]; truncated: boolean }
  | { status: "error" };

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};

const NOTE_BOX: React.CSSProperties = {
  padding: `${spacing[10]}px ${spacing[12]}px`,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
};

const ERROR_BOX: React.CSSProperties = {
  ...NOTE_BOX,
  backgroundColor: semanticColors.destructiveBg,
  border: `1px solid ${semanticColors.destructiveBorder}`,
  color: semanticColors.destructive,
};

const ADVISORY_BOX: React.CSSProperties = {
  ...NOTE_BOX,
  backgroundColor: semanticColors.accentBg,
  border: `1px solid ${semanticColors.border}`,
  color: semanticColors.textSecondary,
};

export function ImportCsvModal({
  onClose,
  onImported,
}: {
  onClose: () => void;
  /** Fired after a REAL import commits, with how many rows landed. The parent
   *  refreshes from the server; nothing is handed back to be inserted
   *  optimistically. */
  onImported: (count: number) => void;
}) {
  // ---- The taxonomy --------------------------------------------------------
  //
  // Loaded rather than restated, exactly as NewProductModal does, and a
  // failure DISABLES the screen. Validating rows against this build's own
  // KNOWN_ lists alone would pass a file whose brand the server has since
  // archived, and the whole batch would then fail after the upload.
  const [taxStatus, setTaxStatus] = useState<"loading" | "ok" | "error">("loading");
  const [taxError, setTaxError] = useState("");
  const [brands, setBrands] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let live = true;
    setTaxStatus("loading");
    (async () => {
      const result = await getTaxonomy();
      if (!live) return;
      if (!result.ok || !result.brands || !result.categories) {
        // CLEARED, not left standing. Stale options under an error message
        // read as current ones and would be validated against.
        setBrands([]);
        setCategories([]);
        setTaxError(result.error ?? "The brand and category list could not be loaded.");
        setTaxStatus("error");
        return;
      }
      setBrands(result.brands);
      setCategories(result.categories);
      setTaxError("");
      setTaxStatus("ok");
    })();
    return () => { live = false; };
  }, [retryTick]);

  const { brandOptions, categoryOptions } = useMemo(
    () => intersectTaxonomy(brands, categories),
    [brands, categories],
  );

  const emptyIntersect = taxStatus === "ok"
    && (brandOptions.length === 0 || categoryOptions.length === 0);

  const emptyMessage = useMemo(
    () => (emptyIntersect
      ? emptyIntersectMessage(brandOptions, categoryOptions, brands, categories)
      : ""),
    [emptyIntersect, brandOptions, categoryOptions, brands, categories],
  );

  // ---- The file ------------------------------------------------------------
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [csvText, setCsvText] = useState("");

  // ---- The send ------------------------------------------------------------
  const [sending, setSending] = useState<"idle" | "validating" | "importing">("idle");
  const [sendError, setSendError] = useState("");
  /** The row a backend error named, if it named one. Rendered ON that row. */
  const [sendErrorRow, setSendErrorRow] = useState<number | undefined>(undefined);

  const busy = sending !== "idle";

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset immediately so picking the SAME file twice still fires onChange --
    // which is exactly what someone does after fixing it in Excel.
    e.target.value = "";
    if (!file) return;

    setFileError("");
    setSendError("");
    setSendErrorRow(undefined);
    setCsvText("");
    setFileName(file.name);

    // The extension AND the type, because neither alone is reliable: Windows
    // reports text/csv, application/vnd.ms-excel or "" for the same file
    // depending on what is installed, so the type is checked only when the
    // extension does not already settle it.
    const isCsvName = /\.csv$/i.test(file.name);
    if (!isCsvName && !ACCEPTED_CSV_TYPES.includes(file.type)) {
      setFileError(
        `${file.name} is not a CSV file. Export the sheet as CSV and pick it again.`,
      );
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      setFileError(
        `${file.name} is ${mb}MB, far larger than a product list of at most `
        + `${MAX_IMPORT_ROWS} rows. Check it is the right file.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setFileError(`Could not read ${file.name}.`);
    reader.onload = () => setCsvText(String(reader.result ?? ""));
    reader.readAsText(file);
  }

  /** ⚠️ RE-RUN WHENEVER THE TAXONOMY CHANGES, not only when the file does. A
   *  Retry that reloads the brand list must re-judge the rows already on
   *  screen; leaving them would show verdicts reached against a list that is
   *  no longer the one a send would be checked against. */
  const result: CsvImportResult | null = useMemo(() => {
    if (csvText === "" || taxStatus !== "ok") return null;
    return parseProductCsv(csvText, brandOptions, categoryOptions);
  }, [csvText, taxStatus, brandOptions, categoryOptions]);

  const blocked = result === null || hasBlockingProblem(result);

  // ---- Duplicates against the catalog --------------------------------------
  //
  // ⚠️ ONE REQUEST, NOT ONE PER ROW. A 200-row file would be 200 sequential
  // round trips (~20s) or 200 parallel ones at a single Odoo worker. One page
  // of the catalog, joined locally, costs the same as opening /catalog does.
  //
  // ⚠️ AND IT REFUSES TO REPORT A NUMBER IT CANNOT STAND BEHIND. If that page
  // came back AT the limit there are parts beyond it that were never compared,
  // so "0 already exist" would be a false negative on precisely the question
  // being asked. That case says the check could not be completed instead.
  const [dupScan, setDupScan] = useState<DupScan>({ status: "idle" });

  useEffect(() => {
    if (!result || result.rows.length === 0) {
      setDupScan({ status: "idle" });
      return;
    }
    let live = true;
    setDupScan({ status: "loading" });
    (async () => {
      const found = await searchProducts("", DUP_SCAN_LIMIT);
      if (!live) return;
      if (!found.ok || !found.products) {
        // A FAILED CHECK SAYS NOTHING, and must not block: it is advisory, so
        // it may neither invent a warning nor suppress one anybody relies on.
        setDupScan({ status: "error" });
        return;
      }
      const existing = new Set(found.products.map((p) => p.partNo));
      setDupScan({
        status: "ok",
        rowNos: catalogDuplicateRowNos(result, existing),
        truncated: found.products.length >= DUP_SCAN_LIMIT,
      });
    })();
    return () => { live = false; };
  }, [result]);

  const dupRowNos = useMemo(
    () => new Set(dupScan.status === "ok" ? dupScan.rowNos : []),
    [dupScan],
  );

  // ---- Closing -------------------------------------------------------------
  //
  // ⚠️ BLOCKED WHILE A SEND IS IN FLIGHT, as NewProductModal is and for a
  // sharper reason: closing mid-import abandons a POST whose result nobody
  // sees, and here that POST may have committed up to 200 products.
  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestClose]);

  // ---- The send ------------------------------------------------------------
  const handleImport = async () => {
    if (!result || blocked || busy) return;

    let specs: Record<string, unknown>[];
    try {
      specs = importSpecs(result);
    } catch (err) {
      // importSpecs throws rather than silently dropping a row, which would
      // shift every later row's position against a positional API.
      setSendError(err instanceof Error ? err.message : String(err));
      return;
    }

    setSendError("");
    setSendErrorRow(undefined);

    /** Turn a backend message into a message plus, when it names one, a row.
     *  Both error forms are handled in parseImportError. */
    const showBackendError = (message: string, stage: string) => {
      const parsed = parseImportError(message);
      if (parsed.rowIndex !== undefined) {
        const row = result.rows.find((r) => r.index === parsed.rowIndex);
        setSendErrorRow(row?.rowNo);
        setSendError(
          row
            ? `${stage} failed at row ${row.rowNo}: ${parsed.message}`
            : `${stage} failed at row ${parsed.rowIndex + 1}: ${parsed.message}`,
        );
        return;
      }
      setSendError(`${stage} failed. ${parsed.message}`);
    };

    // PASS 1 -- the dry run. It creates every row server-side and rolls back,
    // so it proves what this client cannot: that the ORM will actually accept
    // them.
    setSending("validating");
    const check = await importProducts(specs, true);
    if (!check.ok) {
      setSending("idle");
      showBackendError(check.error ?? "The server reported an unknown failure.", "Checking the file");
      return;
    }
    if (check.wouldCreate !== specs.length) {
      // The server counted a different number of rows than were sent. Nothing
      // has been written (this was the dry run), and going on to commit a
      // batch whose size is already in dispute is the wrong move.
      setSending("idle");
      setSendError(
        `The server checked ${check.wouldCreate} rows but ${specs.length} were sent. `
        + `Nothing was imported.`,
      );
      return;
    }

    // PASS 2 -- the real one.
    setSending("importing");
    const done = await importProducts(specs, false);
    setSending("idle");
    if (!done.ok || !done.created) {
      showBackendError(done.error ?? "The server reported an unknown failure.", "The import");
      return;
    }
    onImported(done.created.length);
  };

  function downloadTemplate() {
    // A Blob rather than a data: URI so a long header line cannot run into any
    // URL length limit, and revoked immediately after the click.
    const blob = new Blob([templateCsv()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "anixify-product-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  const rows = result?.rows ?? [];
  const errorRowCount = rows.filter((r) => r.errors.length > 0).length;

  return (
    <div
      data-testid="import-csv-backdrop"
      onClick={requestClose}
      style={{
        position: "fixed", inset: 0, backgroundColor: semanticColors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        data-testid="import-csv-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: layout.importModalWidth,
          // WAS maxWidth: "100%", which is 100% of the backdrop and therefore
          // 100vw -- flush to both edges with no gutter, and it left no room
          // for the box to read as a box. Inert at any width >= 896px.
          maxWidth: overlayMaxWidth,
          maxHeight: "90vh",
          backgroundColor: semanticColors.surface,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          boxShadow: shadows.modal,
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ padding: `${spacing[16]}px ${spacing[20]}px`, borderBottom: `1px solid ${semanticColors.border}` }}
        >
          <div>
            <div
              data-testid="import-csv-title"
              style={{
                fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold,
                color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
              }}
            >
              Import Products from CSV
            </div>
            <div
              style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginTop: spacing[2] }}
            >
              Up to {MAX_IMPORT_ROWS} rows, imported together or not at all.
            </div>
          </div>
          <button
            data-testid="import-csv-close"
            onClick={requestClose}
            disabled={busy}
            title="Close"
            style={{
              width: heights.iconButton, height: heights.iconButton, padding: 0,
              cursor: busy ? "not-allowed" : "pointer",
              background: "transparent", border: "none", color: semanticColors.textSecondary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={iconSize.lg} strokeWidth={iconStroke.regular} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: spacing[20] }}>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>

            {taxStatus === "loading" && (
              <div
                data-testid="import-csv-taxonomy-loading"
                style={{ fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder }}
              >
                Loading brands and categories…
              </div>
            )}

            {/* No fallback to this build's own lists: validating against them
                alone would pass a brand the server has archived, and the whole
                batch would fail after the upload. */}
            {taxStatus === "error" && (
              <div data-testid="import-csv-taxonomy-error" style={ERROR_BOX}>
                <strong>Brands and categories could not be loaded.</strong>{" "}
                <span data-testid="import-csv-taxonomy-error-detail">{taxError}</span>{" "}
                <button
                  data-testid="import-csv-taxonomy-retry"
                  onClick={() => setRetryTick((n) => n + 1)}
                  style={{
                    background: "none", border: "none", padding: 0,
                    color: semanticColors.destructive, textDecoration: "underline",
                    cursor: "pointer", font: "inherit",
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {emptyIntersect && (
              <div data-testid="import-csv-empty-taxonomy" style={ERROR_BOX}>
                {emptyMessage}
              </div>
            )}

            {/* ---- Pick a file ---- */}
            <div>
              <span style={LABEL}>The file</span>
              <input
                ref={fileInputRef}
                data-testid="import-csv-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={pickFile}
                style={{ display: "none" }}
              />
              <div className="flex items-center" style={{ gap: spacing[8], marginTop: spacing[4] }}>
                <button
                  data-testid="import-csv-choose"
                  className="btn-secondary"
                  disabled={taxStatus !== "ok" || emptyIntersect || busy}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    height: heights.control, padding: `0 ${spacing[12]}px`,
                    backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                    cursor: taxStatus === "ok" && !emptyIntersect && !busy ? "pointer" : "not-allowed",
                    display: "inline-flex", alignItems: "center", gap: spacing[6],
                    opacity: taxStatus === "ok" && !emptyIntersect && !busy ? 1 : 0.6,
                  }}
                >
                  <Upload size={iconSize.md} strokeWidth={iconStroke.regular} />
                  {fileName ? "Choose another" : "Choose CSV"}
                </button>
                <span
                  data-testid="import-csv-filename"
                  style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary }}
                >
                  {fileName || "No file chosen"}
                </span>
                <button
                  data-testid="import-csv-template"
                  onClick={downloadTemplate}
                  style={{
                    marginLeft: "auto", background: "none", border: "none", padding: 0,
                    color: semanticColors.accentTextOnTint, textDecoration: "underline",
                    fontSize: typography.fontSize.sm, cursor: "pointer",
                  }}
                >
                  Download template
                </button>
              </div>
              <div
                style={{ marginTop: spacing[6], fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder }}
              >
                Columns: {IMPORT_COLUMNS.join(", ")}. part_number, brand and category are required.
              </div>
            </div>

            {fileError && (
              <div data-testid="import-csv-file-error" style={ERROR_BOX}>{fileError}</div>
            )}

            {/* ---- File-level problems ---- */}
            {result?.fileErrors.map((msg, i) => (
              <div key={i} data-testid="import-csv-file-level-error" style={ERROR_BOX}>
                <div className="flex items-start" style={{ gap: spacing[8] }}>
                  <AlertTriangle
                    size={iconSize.md} strokeWidth={iconStroke.regular}
                    style={{ flexShrink: 0, marginTop: spacing[2] }}
                  />
                  <span>{msg}</span>
                </div>
              </div>
            ))}

            {result?.fileNotices.map((msg, i) => (
              <div key={i} data-testid="import-csv-file-notice" style={ADVISORY_BOX}>{msg}</div>
            ))}

            {/* ---- The catalog duplicate warning ----
                ADVISORY. It never blocks: a part number is legitimately not
                unique, and the endpoint does not dedupe. */}
            {dupScan.status === "ok" && dupScan.rowNos.length > 0 && (
              <div data-testid="import-csv-dup-warning" style={ADVISORY_BOX}>
                <strong>
                  {dupScan.rowNos.length} of these part numbers already exist in the catalog
                </strong>{" "}
                (row{dupScan.rowNos.length === 1 ? "" : "s"} {dupScan.rowNos.join(", ")}).
                Importing creates a SECOND product for each — nothing is merged or
                overwritten. Remove those rows first if that is not what you want.
              </div>
            )}

            {/* ⚠️ NOT "0 already exist". The catalog page came back at its
                limit, so parts beyond it were never compared and the honest
                answer is that the check did not finish. */}
            {dupScan.status === "ok" && dupScan.truncated && (
              <div data-testid="import-csv-dup-truncated" style={ADVISORY_BOX}>
                The catalog is larger than the {DUP_SCAN_LIMIT} products this check reads, so
                the "already exists" check could not be completed. Some of these part
                numbers may already be in the catalog.
              </div>
            )}

            {dupScan.status === "error" && (
              <div data-testid="import-csv-dup-error" style={ADVISORY_BOX}>
                The "already exists" check could not run, so this file has not been
                compared against the catalog. The import itself is unaffected.
              </div>
            )}

            {/* ---- The send failure ---- */}
            {sendError && (
              <div data-testid="import-csv-send-error" style={ERROR_BOX}>
                <div className="flex items-start" style={{ gap: spacing[8] }}>
                  <AlertTriangle
                    size={iconSize.md} strokeWidth={iconStroke.regular}
                    style={{ flexShrink: 0, marginTop: spacing[2] }}
                  />
                  <span>{sendError}</span>
                </div>
              </div>
            )}

            {/* ---- The preview ---- */}
            {rows.length > 0 && (
              <div>
                <div
                  data-testid="import-csv-summary"
                  style={{
                    fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
                    marginBottom: spacing[8],
                  }}
                >
                  <span className="tabular-nums">{rows.length}</span>
                  {rows.length === 1 ? " row" : " rows"}
                  {errorRowCount > 0 && (
                    <span style={{ color: semanticColors.destructive }}>
                      {" "}— <span className="tabular-nums">{errorRowCount}</span>{" "}
                      cannot be imported. Fix the file and choose it again;
                      nothing is sent until every row is valid.
                    </span>
                  )}
                </div>

                <div
                  style={{
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    overflow: "hidden",
                  }}
                >
                  {/* Header */}
                  <div
                    className="flex items-center"
                    style={{
                      height: heights.control, padding: `0 ${spacing[10]}px`, gap: spacing[10],
                      backgroundColor: semanticColors.panelBg,
                      borderBottom: `1px solid ${semanticColors.border}`,
                      fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
                      textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                      fontWeight: typography.fontWeight.medium,
                    }}
                  >
                    <span style={{ width: COL_ROW_NO, flexShrink: 0 }}>Row</span>
                    <span style={{ width: COL_PART_NO, flexShrink: 0 }}>Part No.</span>
                    <span style={{ width: COL_BRAND, flexShrink: 0 }}>Brand</span>
                    <span style={{ width: COL_CATEGORY, flexShrink: 0 }}>Category</span>
                    <span style={{ flex: 1, minWidth: 0 }}>Description / problem</span>
                  </div>

                  {rows.map((row) => {
                    const bad = row.errors.length > 0;
                    const flaggedBySend = sendErrorRow === row.rowNo;
                    return (
                      <div
                        key={row.rowNo}
                        data-testid={`import-csv-row-${row.rowNo}`}
                        style={{
                          padding: `${spacing[6]}px ${spacing[10]}px`,
                          borderBottom: `1px solid ${semanticColors.border}`,
                          backgroundColor: bad || flaggedBySend
                            ? semanticColors.destructiveBg
                            : semanticColors.surface,
                        }}
                      >
                        <div className="flex items-baseline" style={{ gap: spacing[10] }}>
                          <span
                            className="tabular-nums"
                            style={{
                              width: COL_ROW_NO, flexShrink: 0,
                              fontSize: typography.fontSize.sm, color: semanticColors.textMuted,
                            }}
                          >
                            {row.rowNo}
                          </span>
                          <span
                            style={{
                              width: COL_PART_NO, flexShrink: 0, overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                              fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                            }}
                          >
                            {row.values.part_number || "—"}
                          </span>
                          <span
                            style={{
                              width: COL_BRAND, flexShrink: 0, overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                              fontSize: typography.fontSize.base, color: semanticColors.textSecondary,
                            }}
                          >
                            {row.values.brand || "—"}
                          </span>
                          <span
                            style={{
                              width: COL_CATEGORY, flexShrink: 0, overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                              fontSize: typography.fontSize.base, color: semanticColors.textSecondary,
                            }}
                          >
                            {row.values.category || "—"}
                          </span>
                          <span
                            style={{
                              flex: 1, minWidth: 0, overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                              fontSize: typography.fontSize.base, color: semanticColors.textSecondary,
                            }}
                          >
                            {row.values.description || "—"}
                          </span>
                        </div>

                        {row.errors.map((err, i) => (
                          <div
                            key={i}
                            data-testid={`import-csv-row-${row.rowNo}-error`}
                            style={{
                              marginTop: spacing[4], marginLeft: COL_ROW_NO + spacing[10],
                              fontSize: typography.fontSize.xs, color: semanticColors.destructive,
                            }}
                          >
                            {err}
                          </div>
                        ))}

                        {/* Amber and non-blocking, the same treatment the
                            GSTIN advisory and the single-create duplicate
                            warning already get. */}
                        {row.notices.map((note, i) => (
                          <div
                            key={i}
                            data-testid={`import-csv-row-${row.rowNo}-notice`}
                            style={{
                              marginTop: spacing[4], marginLeft: COL_ROW_NO + spacing[10],
                              fontSize: typography.fontSize.xs, color: colors.amber600,
                            }}
                          >
                            {note}
                          </div>
                        ))}

                        {dupRowNos.has(row.rowNo) && (
                          <div
                            data-testid={`import-csv-row-${row.rowNo}-dup`}
                            style={{
                              marginTop: spacing[4], marginLeft: COL_ROW_NO + spacing[10],
                              fontSize: typography.fontSize.xs, color: colors.amber600,
                            }}
                          >
                            This part number already exists in the catalog. Importing adds
                            a second product; it does not update the first.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${semanticColors.border}`,
            padding: spacing[16],
            display: "flex", gap: spacing[8], justifyContent: "flex-end", alignItems: "center",
          }}
        >
          {busy && (
            <span
              data-testid="import-csv-busy"
              style={{ marginRight: "auto", fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}
            >
              {sending === "validating"
                ? "Checking the file against the server…"
                : "Importing… don't close this window."}
            </span>
          )}
          <button
            data-testid="import-csv-cancel"
            className="btn-secondary"
            onClick={requestClose}
            disabled={busy}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              fontSize: typography.fontSize.base, cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="import-csv-submit"
            className="btn-primary"
            onClick={handleImport}
            disabled={blocked || busy}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: !blocked && !busy ? colors.indigo600 : semanticColors.textDisabled,
              color: semanticColors.onAccent, border: "none", borderRadius: radius.base,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: !blocked && !busy ? "pointer" : "not-allowed",
            }}
          >
            {sending === "validating" ? "Checking…"
              : sending === "importing" ? "Importing…"
              : rows.length > 0 ? `Import ${rows.length} ${rows.length === 1 ? "product" : "products"}`
              : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
